import React, { Suspense, lazy } from 'react';
import {
  getReportingMonth,
  type ReportingMonth,
  formatReportingMonthLabelBuddhist,
  reportingMonthToFiscalYearQuarter,
  buddhistFiscalYear,
  fiscalQuarterOptionLabel,
  fiscalYearQuarterToAnchorReportingMonth,
  reportingMonthToFiscalQuarterAnchor,
  getFiscalQuarterMonths,
  canonicalReportingMonth,
  type FiscalQuarter,
} from '../lib/reportingMonth';
import { resolveScopedDepartments } from '../lib/departments';
import { type RoleContext, roleIndicatesSupervisorNurse } from '../lib/roles';
import { type RoleScope } from '../services/auth/roleResolver';
import {
  fetchReportingData,
  computeAggregates,
  pooledMonthlyCompletenessPct,
  getReportingSubmissionCount,
  hasReportingAggregateData,
  type ReportingFetchOptions,
  type ReportingAggregateResult,
  type ReportingSubmission,
  type ReportingScopeOverrides,
  getReportingResultsForMonths,
  getYearlyReportingResults,
  fetchMonthlyReportingData,
} from '../services/dashboardData';
import { reportingDashboardErrorMessage } from '../lib/reportingDashboardErrors';
import { NIDATA_LOADING_LABEL } from '../lib/nidataUiCopy';
import {
  buildDepartmentIdToNameMap,
  compareDepartmentsByLofThenName,
  filterDepartmentsForUi,
  filterSubmissionsForUi,
  isDepartmentHiddenInUi,
  looksLikeDepartmentId,
  resolveDepartmentDisplayName,
  resolveSubmissionDepartmentDisplayName,
  sortDepartmentsByLofThenName,
} from '../lib/departmentUi';
import { isNeedleAndSharpDepartmentExcluded } from '../lib/needleAndSharpPolicy';
import { isSirenBundleDepartmentExcluded } from '../lib/sirenBundlePolicy';
import { normalizeSubmissionShape } from '../services/submissionShape';
import { useDepartments } from '../contexts/useDepartments';
import { useComplianceCategories } from '../hooks/useComplianceCategories';
import { DEFAULT_COMPLIANCE_CATEGORIES } from '../lib/complianceCategories';
import { useAuth } from '../contexts/useAuth';
import { formatThaiMonthYYBuddhist, getYearMonthsFromReportingMonth } from '../utils/dateThai';
import { getUserAssignedDepartmentIds } from '../lib/userDepartmentScope';
import { departmentIdScopeKey, normalizeDepartmentId } from '../utils/normalizeIds';
import { CROSS_AUDIT_COLOR } from '../lib/reportingColors';
import {
  METRICS_KEY_CHART1_CONSISTENCY_PCT,
  METRICS_KEY_OVERALL_CONSISTENCY_PCT,
} from '../lib/submissionMetrics';
import { completenessFromUnknownRow } from '../lib/nursingComplianceScoring';
import * as HoverCard from '@radix-ui/react-hover-card';
import { Info } from 'lucide-react';
import { safeLazyImport } from '../utils/safeLazyImport';

const EthicReportingTable = lazy(safeLazyImport(() => import('./EthicReportingTable').then((m) => ({ default: m.EthicReportingTable }))));
const OrgClimateReportingTable = lazy(safeLazyImport(() => import('./OrgClimateReportingTable').then((m) => ({ default: m.OrgClimateReportingTable }))));
const MedErrorIncidentDashboard = lazy(safeLazyImport(() => import('./MedErrorIncidentDashboard')));
const FallIncidentDashboard = lazy(safeLazyImport(() => import('./FallIncidentDashboard')));

const categoryDashboardFallback = (
  <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white/60 p-12">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
  </div>
);
import { useSupervisorReportingFilters } from '../contexts/SupervisorReportingFiltersContext';
import { hasTeamCategoryHospitalWideAccess } from '../lib/reportingTeamCategoryScope';
import { filterReportingSubmissionsByClientDepartments } from '../lib/reportingDepartmentClientFilter';
import { resolveNurseNotesReportingScope } from '../lib/nurseNotesReportingScope';
import { canViewOrgClimateSurveys } from '../lib/orgClimateSurveys/access';
import {
  isMedErrorSupervisionCategory,
  reportingMetricColumnsForCategory,
} from '../lib/reportingMetricColumns';

type ViewMode = 'monthly' | 'trimester' | 'year' | 'yearly';

const EXTRA = {
  diseaseScoreKey: 'disease_monthly_avg_score', // Average per "รายโรค" chart (คะแนนรายโรค)
  consistencyKey: null,
};

type ReportingTableProps = {
  roleScope: RoleScope | null;
  roleContext: RoleContext | null;
  canEdit?: boolean;
  onEditSubmission?: (submission: ReportingSubmission) => void;
  refreshKey?: number;
  reportingScope?: ReportingScopeOverrides;
  reportingOptions?: ReportingFetchOptions;
  mode?: 'default' | 'team';
  teamAllowedCategoryIds?: string[];
  /** When true, hide internal filter bar and department pills (used when top bar controls both dashboard and table). */
  hideInternalFilters?: boolean;
  /** Team page (/team): client-side filter after fetch. */
  teamDepartmentClientFilterIds?: string[];
  /** Sticky มุมมอง + เดือนรายงาน in the shell controls this table. */
  shellNurseNotesPeriodControls?: boolean;
};

const formatPeriodicityLabel = (periodicity?: string) => {
  switch (periodicity?.toLowerCase()) {
    case 'weekly':
      return 'รายสัปดาห์';
    case 'quarterly':
      return 'รายไตรมาส';
    case 'yearly':
      return 'รายปี';
    case 'monthly':
      return 'รายเดือน';
    default:
      return periodicity;
  }
};

const ETHIC_LABEL = 'บรรยากาศองค์กรจริยธรรม';
const ORG_CLIMATE_LABEL = 'แบบประเมินบรรยากาศจริยธรรมในองค์การพยาบาล';
const FALL_INCIDENT_LABEL = 'จำนวนอุบัติการณ์การพลัดตกหกล้ม';
const isEthicCategory = (value?: string | null) => value === 'ethicSurveys' || value === ETHIC_LABEL;
const isOrgClimateCategory = (value?: string | null) =>
  value === 'orgClimateSurveys' || value === ORG_CLIMATE_LABEL;
const isMedErrorIncidentCategory = (value?: string | null) => value === 'med_error_incident' || value === 'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา';
const isFallIncidentCategory = (value?: string | null) => value === 'fall_incident' || value === FALL_INCIDENT_LABEL;
const normalizeCategoryId = (value: string) => {
  if (value === ETHIC_LABEL) return 'ethicSurveys';
  if (value === ORG_CLIMATE_LABEL) return 'orgClimateSurveys';
  return value;
};
const toRoleToken = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized || null;
  }
  if (value && typeof value === 'object' && 'role' in value) {
    const roleValue = (value as { role?: unknown }).role;
    if (typeof roleValue === 'string') {
      const normalized = roleValue.trim().toLowerCase();
      return normalized || null;
    }
  }
  return null;
};
const collectRoleTokens = (values: unknown[]): string[] =>
  values.map((value) => toRoleToken(value)).filter((token): token is string => Boolean(token));
const sanitizeScopedDepartmentIds = (ids?: string[]) => {
  const normalized = Array.from(
    new Set(
      (ids ?? [])
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean),
    ),
  );
  return normalized.includes('*') ? ['*'] : normalized;
};

const isExtensionNoiseError = (error: unknown) => {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (!message) return false;
  return (
    message.includes('triggerAutofillScriptInjection') ||
    message.includes('background.js') ||
    message.includes('ERR_BLOCKED_BY_CLIENT')
  );
};

const toDisplayNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value.trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const formatScoreValue = (value: number | null | undefined | string) => {
  const n = toDisplayNumber(value);
  if (n === null) return 'NA';
  return n.toFixed(2);
};

const formatCountValue = (value: number | null | undefined | string) => {
  const n = toDisplayNumber(value);
  if (n === null) return 'NA';
  return String(Math.round(n));
};

const shouldExcludeChartTitleForOwnSubmission = (title: string) => {
  if (title.includes('ตรวจไขว้') || title.includes('แก้ไข')) return true;
  const lowered = title.toLowerCase();
  return (
    lowered.includes('edit') ||
    lowered.includes('audit') ||
    lowered.includes('cross audit')
  );
};

const buildIncludedChartIndexes = (
  answers: unknown[][],
  chartTitles: string[],
  isCrossAudit: boolean,
): number[] => {
  if (!isCrossAudit && chartTitles.length) {
    return answers.flatMap((_, idx) => {
      const title = typeof chartTitles[idx] === 'string' ? chartTitles[idx] : '';
      return title && !shouldExcludeChartTitleForOwnSubmission(title) ? [idx] : [];
    });
  }
  return answers.map((_, idx) => idx);
};

/** Pooled ร้อยละความสมบูรณ์รายโรค (0–100), aligned with deriveSubmissionMetrics disease charts. */
const pooledDiseaseCompletenessPct = (submissions: ReportingSubmission[]): number | null => {
  const values: number[] = [];
  for (const sub of submissions) {
    const metrics = (sub.metrics ?? {}) as Record<string, unknown>;
    const stored = typeof metrics.disease_pct === 'number' && Number.isFinite(metrics.disease_pct)
      ? metrics.disease_pct
      : Number.isFinite(Number(metrics.disease_pct))
        ? Number(metrics.disease_pct)
        : null;
    if (stored !== null) {
      values.push(stored);
      continue;
    }
    const raw = sub as Record<string, unknown>;
    const shape = normalizeSubmissionShape(raw);
    const answers = shape.answers.map((row) => (Array.isArray(row) ? row : []));
    const chartTitles = shape.chartTitles.map((t) => (typeof t === 'string' ? t : String(t ?? '')));
    const isCrossAudit = !!(sub.isCrossAudit || raw.crossAudit === true);
    const includedIndexes = buildIncludedChartIndexes(answers, chartTitles, isCrossAudit);
    let score = 0;
    let full = 0;
    for (const rowIndex of includedIndexes) {
      const title = chartTitles[rowIndex] ?? '';
      if (!title.includes('รายโรค')) continue;
      const { score: rs, full: rf } = completenessFromUnknownRow(answers[rowIndex]);
      score += rs;
      full += rf;
    }
    if (full > 0) values.push(Math.round((score / full) * 10000) / 100);
  }
  if (values.length === 0) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
};

const pooledConsistencyPctFromSubmissions = (submissions: ReportingSubmission[]): number | null => {
  const { averages } = computeAggregates(submissions);
  const v =
    averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT] ?? averages[METRICS_KEY_CHART1_CONSISTENCY_PCT];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

const visibleCategoryLabel = (title: unknown, id: unknown): string => {
  const titleText = safeText(title).trim();
  const idText = safeText(id).trim();
  if (titleText && titleText !== idText && !looksLikeDepartmentId(titleText)) return titleText;
  const match = DEFAULT_COMPLIANCE_CATEGORIES.find(
    (category) => category.id === idText || category.id === titleText || category.title === titleText,
  );
  if (match?.title) return match.title;
  if (titleText && !looksLikeDepartmentId(titleText)) return titleText;
  return 'ไม่ทราบชื่อหมวด';
};

const safeText = (value: unknown, fallback = ''): string => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const refLike = value as { id?: unknown; path?: unknown };
    if (typeof refLike.id === 'string' && refLike.id.trim()) return refLike.id.trim();
    if (typeof refLike.path === 'string' && refLike.path.trim()) {
      const last = refLike.path.split('/').filter(Boolean).pop();
      if (last) return last;
    }
  }
  try {
    return String(value);
  } catch {
    return fallback || '—';
  }
};

/** Extract a single disease string from various shapes */
const toStringDisease = (v: unknown): string | null => {
  if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
};

/**
 * Get ALL disease names (ชื่อรายโรค) from a submission – from every chart's disease field
 * and legacy data.disease. Returns unique, comma-separated string for display.
 */
const getAllDiseasesFromSubmission = (submission: ReportingSubmission): string[] => {
  const s = submission as Record<string, unknown>;
  const seen = new Set<string>();
  const add = (v: string | null) => {
    if (v && !seen.has(v)) seen.add(v);
  };

  // Top-level: submission.disease (array from form) or submission.charts[].disease
  const topDisease = s.disease;
  if (Array.isArray(topDisease)) {
    topDisease.forEach((v) => add(toStringDisease(v)));
  } else {
    add(toStringDisease(topDisease));
  }

  const topCharts = s.charts;
  if (Array.isArray(topCharts)) {
    topCharts.forEach((c) => {
      const chart = c as Record<string, unknown> | undefined;
      add(toStringDisease(chart?.disease));
    });
  }

  // Nested: submission.data (some flows store raw doc under .data)
  const data = s.data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    const dataDisease = d.disease;
    if (Array.isArray(dataDisease)) {
      dataDisease.forEach((v) => add(toStringDisease(v)));
    } else {
      add(toStringDisease(dataDisease));
    }
    const dataCharts = d.charts;
    if (Array.isArray(dataCharts)) {
      dataCharts.forEach((c) => {
        const chart = c as Record<string, unknown> | undefined;
        add(toStringDisease(chart?.disease));
      });
    }
  }

  return Array.from(seen);
};

/**
 * Get disease names with occurrence counts across submissions (per month).
 * Returns unique names for display and a count map for tooltips.
 */
const getDiseaseCountsFromSubmissions = (
  submissions: ReportingSubmission[],
): { unique: string[]; counts: Map<string, number> } => {
  const counts = new Map<string, number>();
  const add = (v: string | null) => {
    if (!v) return;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  };

  submissions.forEach((s) => {
    const sub = s as Record<string, unknown>;
    const topDisease = sub.disease;
    if (Array.isArray(topDisease)) {
      topDisease.forEach((v) => add(toStringDisease(v)));
    } else {
      add(toStringDisease(topDisease));
    }
    const topCharts = sub.charts;
    if (Array.isArray(topCharts)) {
      topCharts.forEach((c) => {
        const chart = c as Record<string, unknown> | undefined;
        add(toStringDisease(chart?.disease));
      });
    }
    const data = sub.data;
    if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      const dataDisease = d.disease;
      if (Array.isArray(dataDisease)) {
        dataDisease.forEach((v) => add(toStringDisease(v)));
      } else {
        add(toStringDisease(dataDisease));
      }
      const dataCharts = d.charts;
      if (Array.isArray(dataCharts)) {
        dataCharts.forEach((c) => {
          const chart = c as Record<string, unknown> | undefined;
          add(toStringDisease(chart?.disease));
        });
      }
    }
  });

  return { unique: Array.from(counts.keys()), counts };
};

const ReportingTable: React.FC<ReportingTableProps> = ({
  roleContext,
  roleScope,
  canEdit = false,
  onEditSubmission,
  refreshKey = 0,
  reportingScope,
  reportingOptions,
  mode = 'default',
  teamAllowedCategoryIds,
  hideInternalFilters = false,
  teamDepartmentClientFilterIds,
  shellNurseNotesPeriodControls = false,
}) => {
  const [view, setView] = React.useState<ViewMode>('yearly');
  const [selectedMonth, setSelectedMonth] = React.useState<ReportingMonth>(() => getReportingMonth(new Date()));
  const [categoryId, setCategoryId] = React.useState<string>('');
  const [selectedDepartmentId, setSelectedDepartmentId] = React.useState<string>('');
  const [result, setResult] = React.useState<ReportingAggregateResult | null>(null);
  const [yearlyResults, setYearlyResults] = React.useState<
    Array<{ reportingMonth: ReportingMonth; result: ReportingAggregateResult }> | null
  >(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [summaryMonthTab, setSummaryMonthTab] = React.useState<ReportingMonth | null>(null);
  const [monthDetailCache, setMonthDetailCache] = React.useState<
    Partial<Record<ReportingMonth, ReportingSubmission[]>>
  >({});
  const [monthDetailLoading, setMonthDetailLoading] = React.useState(false);
  const monthDetailLoadedRef = React.useRef<Set<string>>(new Set());
  const sharedFilters = useSupervisorReportingFilters();
  const sharedCategoryId = sharedFilters?.categoryId ?? '';
  // Use shared filters when the top filter bar controls both Dashboard and Table (SUPERVISOR_REPORTING_TAB).
  const useSharedFilters = !!sharedFilters && hideInternalFilters;
  /** Dashboard sets this while fetch runs; table has its own `loading` — hide empty-state until both are idle to avoid scary "0 submissions" during top-section load. */
  const sharedDashboardLoading = useSharedFilters ? Boolean(sharedFilters?.loading) : false;
  const useSharedFiltersNurseNotes =
    useSharedFilters &&
    sharedCategoryId === 'nurse_notes' &&
    !isEthicCategory(sharedCategoryId);
  /** Sticky กรองหน่วยงาน. Empty means ทั้งหมด. */
  const shellNurseNotesDeptIds = React.useMemo(() => {
    if (sharedCategoryId !== 'nurse_notes') return [] as string[];
    return (sharedFilters?.nurseNotesDeptFilter ?? []).map((id) => String(id).trim()).filter(Boolean);
  }, [sharedCategoryId, sharedFilters?.nurseNotesDeptFilter]);
  const effectiveCategoryId = useSharedFilters ? sharedCategoryId : categoryId;
  const teamCategoryHospitalWide = React.useMemo(
    () => hasTeamCategoryHospitalWideAccess(roleContext, effectiveCategoryId || undefined),
    [roleContext, effectiveCategoryId],
  );
  const useShellNurseNotesPeriod =
    !!sharedFilters &&
    mode !== 'team' &&
    (shellNurseNotesPeriodControls ||
      sharedCategoryId === 'nurse_notes' ||
      effectiveCategoryId === 'nurse_notes' ||
      effectiveCategoryId === 'บันทึกทางการพยาบาล');
  const effectiveSelectedMonth =
    useSharedFilters || useShellNurseNotesPeriod ? sharedFilters!.reportingMonth : selectedMonth;
  const effectiveView = useSharedFilters || useShellNurseNotesPeriod ? sharedFilters!.view : view;
  /** รายปี with 12 detail rows (standalone); supervisor “รายปี” uses aggregate `year`. */
  const showsTwelveMonthYearBreakdownTable = effectiveView === 'yearly';
  const { departments } = useDepartments();
  const { categories: complianceCategories } = useComplianceCategories();
  const authContext = useAuth();
  const canViewOrgClimate = canViewOrgClimateSurveys(authContext.authUser?.uid);
  const effectiveLofDepartmentIds = React.useMemo(
    () => authContext.dataScope?.lineOfWorkDepartmentIds ?? roleContext?.lineOfWorkDepartmentIds ?? [],
    [authContext.dataScope?.lineOfWorkDepartmentIds, roleContext?.lineOfWorkDepartmentIds],
  );
  const userAssignedDepartmentIds = React.useMemo(
    () => getUserAssignedDepartmentIds(authContext.firestoreUser),
    [authContext.firestoreUser],
  );
  const userAssignedDepartmentIdSet = React.useMemo(
    () => new Set(userAssignedDepartmentIds.map((id) => normalizeDepartmentId(id))),
    [userAssignedDepartmentIds],
  );
  const userAssignedDepartmentIdsForFirestore = React.useMemo(() => {
    if (!userAssignedDepartmentIds.length) return [];
    const idByNormalized = new Map<string, string>();
    departments.forEach((dept) => {
      idByNormalized.set(normalizeDepartmentId(dept.id), dept.id);
    });
    const resolved = userAssignedDepartmentIds
      .map((id) => idByNormalized.get(normalizeDepartmentId(id)) ?? id)
      .filter(Boolean);
    return Array.from(new Set(resolved));
  }, [departments, userAssignedDepartmentIds]);

  const scopedDepartments = React.useMemo(
    () =>
      resolveScopedDepartments(
        departments,
        roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? [],
        effectiveLofDepartmentIds,
      ),
    [departments, effectiveLofDepartmentIds, roleScope?.departmentIds, roleScope?.view?.departmentIds],
  );

  const isSupervisorNurse = React.useMemo(() => {
    if (!roleContext) return false;
    const roles = collectRoleTokens([
      roleContext.generalRole,
      roleContext.positionRole,
      roleContext.departmentRole,
      ...(roleContext.roles ?? []),
      ...(roleContext.departmentRoles ?? []),
      roleContext.teamRole,
      ...(roleContext.teamRoles ?? []),
    ]);
    return roles.some((role) => roleIndicatesSupervisorNurse(role));
  }, [roleContext]);

  // Detect head ward nurse role - they should only see their own submissions, not cross audits
  const isHeadWardNurse = React.useMemo(() => {
    if (!roleContext) return false;
    const roles = collectRoleTokens([
      roleContext.generalRole,
      roleContext.positionRole,
      roleContext.departmentRole,
      ...(roleContext.roles ?? []),
      ...(roleContext.departmentRoles ?? []),
    ]);
    return roles.some((role) => role.includes('head_ward') || role.includes('hwn'));
  }, [roleContext]);

  const hasWildcardScope = React.useMemo(
    () =>
      (roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? []).includes('*'),
    [roleScope?.departmentIds, roleScope?.view?.departmentIds],
  );
  const canUseAllDepartmentDefault = React.useMemo(
    () => mode === 'team' || isSupervisorNurse || hasWildcardScope || teamCategoryHospitalWide,
    [mode, isSupervisorNurse, hasWildcardScope, teamCategoryHospitalWide],
  );

  const scopedDepartmentsForUser = React.useMemo(() => {
    const resolve = (): typeof departments => {
      if (teamCategoryHospitalWide) return departments;
      // On team page with wildcard scope, NN and other team roles see ALL departments
      if (
        mode === 'team' &&
        reportingScope &&
        Array.isArray(reportingScope.departmentIds) &&
        reportingScope.departmentIds.includes('*')
      ) {
        return departments;
      }
      // Normal page: when reportingScope has explicit departmentIds (from parent LOF selection),
      // restrict to those - prevents showing all departments for supervisor_nurse
      if (
        mode === 'default' &&
        reportingScope &&
        Array.isArray(reportingScope.departmentIds) &&
        reportingScope.departmentIds.length > 0 &&
        !reportingScope.departmentIds.includes('*')
      ) {
        const allowedIds = new Set(
          reportingScope.departmentIds.map((id) => normalizeDepartmentId(id)).filter(Boolean),
        );
        if (allowedIds.size > 0) {
          const restricted = scopedDepartments.filter((dept) =>
            allowedIds.has(normalizeDepartmentId(dept.id)),
          );
          if (restricted.length > 0) return restricted;
        }
      }
      // MANAGER, AUDITOR, ADMIN: same view as SUPERVISOR_NURSE but for every department (no LOF filter)
      if (hasWildcardScope) return scopedDepartments;
      // Supervisor nurses see all LOF departments (from roleScope), never filter by user-assigned only
      if (mode === 'default' && isSupervisorNurse) return scopedDepartments;
      if (mode !== 'default' || userAssignedDepartmentIdSet.size === 0) return scopedDepartments;
      return scopedDepartments.filter((dept) => userAssignedDepartmentIdSet.has(normalizeDepartmentId(dept.id)));
    };
    const resolved = sortDepartmentsByLofThenName(filterDepartmentsForUi(resolve()));
    const sirenReporting =
      effectiveCategoryId === 'siren_bundle' || effectiveCategoryId === 'SIREN bundle';
    const needleReporting =
      effectiveCategoryId === 'needleandsharp' || effectiveCategoryId === 'เข็มตำ/ของมีคมบาด';
    if (sirenReporting) return resolved.filter((dept) => !isSirenBundleDepartmentExcluded(dept.id));
    if (needleReporting) return resolved.filter((dept) => !isNeedleAndSharpDepartmentExcluded(dept.id));
    return resolved;
  }, [
    mode,
    scopedDepartments,
    userAssignedDepartmentIdSet,
    reportingScope,
    departments,
    isSupervisorNurse,
    hasWildcardScope,
    teamCategoryHospitalWide,
    effectiveCategoryId,
  ]);

  const departmentIdToName = React.useMemo(() => buildDepartmentIdToNameMap(departments), [departments]);

  const categoryOptions = React.useMemo(() => {
    const nursingCat = DEFAULT_COMPLIANCE_CATEGORIES.find(
      (c) => c.id === 'nurse_notes' || c.title === 'บันทึกทางการพยาบาล',
    );
    const ethicCat = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'ethicSurveys');
    const orgClimateCat = canViewOrgClimate
      ? DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'orgClimateSurveys')
      : undefined;
    const medErrorCat = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'med_error');
    const sirenCat = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'siren_bundle');
    const needleCat = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'needleandsharp');
    const medErrorIncidentCat = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'med_error_incident');
    const fallIncidentCat = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'fall_incident');
    let uniqueList: typeof DEFAULT_COMPLIANCE_CATEGORIES = [];
    if (nursingCat) uniqueList.push(nursingCat);
    if (ethicCat) uniqueList.push(ethicCat);
    if (orgClimateCat) uniqueList.push(orgClimateCat);
    if (medErrorCat) uniqueList.push(medErrorCat);
    if (sirenCat) uniqueList.push(sirenCat);
    if (needleCat) uniqueList.push(needleCat);
    // Import dashboards (med error / fall) stay in the team-page picker only; default "ตารางข้อมูล" is submissions + ethic.
    if (mode === 'team') {
      if (medErrorIncidentCat) uniqueList.push(medErrorIncidentCat);
      if (fallIncidentCat) uniqueList.push(fallIncidentCat);
      const allowedSet = new Set((teamAllowedCategoryIds ?? []).filter(Boolean));
      if (allowedSet.size) {
        uniqueList = uniqueList.filter((category) => allowedSet.has(category.id ?? category.title ?? ''));
      }
    }

    return uniqueList;
  }, [mode, teamAllowedCategoryIds, canViewOrgClimate]);
  const hasEthicCategoryOption = React.useMemo(
    () =>
      categoryOptions.some(
        (category) =>
          (category.id ?? category.title) === 'ethicSurveys' ||
          category.title === 'บรรยากาศองค์กรจริยธรรม',
      ),
    [categoryOptions],
  );
  const ethicDefault = React.useMemo(
    () => DEFAULT_COMPLIANCE_CATEGORIES.find((category) => category.id === 'ethicSurveys'),
    [],
  );
  const ethicTitle = ethicDefault?.title ?? 'บรรยากาศองค์กรจริยธรรม';
  const ethicPeriodicityLabel = formatPeriodicityLabel(ethicDefault?.periodicity);

  const effectiveReportingOptions = React.useMemo<ReportingFetchOptions>(
    () => ({
      ...reportingOptions,
      mode,
      teamAllowedCategoryIds,
    }),
    [mode, reportingOptions, teamAllowedCategoryIds],
  );

  const filterSubmissionsByDepartment = React.useCallback(
    (submissions: ReportingSubmission[], options?: { reportingMonth?: ReportingMonth }) => {
      let filtered = submissions;

      // Filter by selected month
      const month = options?.reportingMonth ?? effectiveSelectedMonth;
      filtered = filtered.filter((s) => s.reportingMonth === month);

      // In default reporting table, restrict to the user's explicitly assigned departments (not LOF-expanded).
      // Supervisor nurses see ALL departments in their LOF, so skip this filter for them.
      // Cross-audits: include if user has access to audited OR auditor department.
      // Apply data source filter for Head Ward Nurse (ส่งเอง only)
      if (isHeadWardNurse) {
        filtered = filtered.filter((s) => !(s.isCrossAudit || (s as any).crossAudit));
      }

      if (
        mode === 'default' &&
        !isSupervisorNurse &&
        !hasWildcardScope &&
        !teamCategoryHospitalWide &&
        userAssignedDepartmentIdSet.size > 0
      ) {
        filtered = filtered.filter((s) => {
          const isCross = s.isCrossAudit || (s as any).crossAudit;
          if (isCross) {
            const audited = normalizeDepartmentId((s as any).auditedDepartmentId ?? '');
            const auditor = normalizeDepartmentId((s as any).auditorDepartmentId ?? '');
            return (audited && userAssignedDepartmentIdSet.has(audited)) || (auditor && userAssignedDepartmentIdSet.has(auditor));
          }
          const deptId = (s.departmentId ?? s.departmentName ?? '').trim();
          if (!deptId) return false;
          return userAssignedDepartmentIdSet.has(normalizeDepartmentId(deptId));
        });
      }

      // Filter by selected department if specified
      if (selectedDepartmentId) {
        filtered = filtered.filter(
          (s) =>
            normalizeDepartmentId((s.departmentId ?? '').trim()) === normalizeDepartmentId(selectedDepartmentId) ||
            normalizeDepartmentId((s.departmentName ?? '').trim()) === normalizeDepartmentId(selectedDepartmentId),
        );
      }

      filtered = filterReportingSubmissionsByClientDepartments(filtered, teamDepartmentClientFilterIds);
      filtered = filterReportingSubmissionsByClientDepartments(filtered, shellNurseNotesDeptIds);

      const sirenReporting =
        effectiveCategoryId === 'siren_bundle' || effectiveCategoryId === 'SIREN bundle';
      if (sirenReporting) {
        filtered = filtered.filter((row) => !isSirenBundleDepartmentExcluded(row.departmentId));
      }

      return filtered;
    },
    [
      selectedDepartmentId,
      effectiveSelectedMonth,
      effectiveCategoryId,
      hasWildcardScope,
      isHeadWardNurse,
      isSupervisorNurse,
      mode,
      userAssignedDepartmentIdSet,
      teamDepartmentClientFilterIds,
      teamCategoryHospitalWide,
      shellNurseNotesDeptIds,
    ],
  );

  const filteredResult = React.useMemo(() => {
    if (!result) return null;
    const filtered = filterSubmissionsByDepartment(result.submissions);
    const { totals, averages } = computeAggregates(filtered);
    return { ...result, submissions: filtered, totals, averages, submissionsCount: filtered.length };
  }, [result, filterSubmissionsByDepartment]);

  const filteredYearlyResults = React.useMemo(() => {
    if (!yearlyResults) return null;
    return yearlyResults.map((entry) => {
      const rawSubs = entry.result.submissions ?? [];
      // Summary aggregates strip bodies — keep server counts/totals (do not recompute from []).
      // SIREN totals include wards the screen hides, so a body-less month cannot keep them.
      if (rawSubs.length === 0 && hasReportingAggregateData(entry.result)) {
        const sirenReporting =
          effectiveCategoryId === 'siren_bundle' || effectiveCategoryId === 'SIREN bundle';
        if (!sirenReporting) return entry;
        return {
          reportingMonth: entry.reportingMonth,
          result: {
            ...entry.result,
            submissions: [],
            totals: {},
            averages: {},
            submissionsCount: 0,
          },
        };
      }
      const filtered = filterSubmissionsByDepartment(rawSubs, {
        reportingMonth: entry.reportingMonth,
      });
      const { totals, averages } = computeAggregates(filtered);
      return {
        reportingMonth: entry.reportingMonth,
        result: {
          ...entry.result,
          submissions: filtered,
          totals,
          averages,
          submissionsCount: filtered.length,
        },
      };
    });
  }, [yearlyResults, filterSubmissionsByDepartment, effectiveCategoryId]);

  const displayResult = filteredResult ?? result;

  const emptyStateVisible =
    !loading &&
    !sharedDashboardLoading &&
    !error &&
    ((
      (effectiveView === 'yearly' || effectiveView === 'year' || effectiveView === 'trimester') &&
      filteredYearlyResults !== null &&
      filteredYearlyResults.every((r) => !hasReportingAggregateData(r.result))
    ) ||
      (effectiveView !== 'yearly' &&
        effectiveView !== 'year' &&
        effectiveView !== 'trimester' &&
        (displayResult ?? result) !== null &&
        !hasReportingAggregateData(displayResult ?? result ?? undefined)));
  const scopedDepartmentLabels = React.useMemo(
    () =>
      scopedDepartmentsForUser
        .map((department) =>
          resolveDepartmentDisplayName(
            safeText(department.id),
            safeText((department as { name?: unknown }).name),
          ),
        )
        .filter((label) => label.trim().length > 0),
    [scopedDepartmentsForUser],
  );
  const selectedCategoryLabel = React.useMemo(() => {
    if (!categoryId) return null;
    const match = categoryOptions.find((option) => option.id === categoryId || option.title === categoryId);
    return visibleCategoryLabel(match?.title, match?.id ?? categoryId);
  }, [categoryId, categoryOptions]);

  const metricColumns = React.useMemo(
    () => reportingMetricColumnsForCategory(effectiveCategoryId),
    [effectiveCategoryId],
  );
  const isMedErrorSupervisionTable = isMedErrorSupervisionCategory(effectiveCategoryId);
  const showNurseNotesSummaryColumns =
    effectiveCategoryId === 'nurse_notes' || effectiveCategoryId === 'บันทึกทางการพยาบาล';
  const departmentSummaryColumnCount = showNurseNotesSummaryColumns ? 4 : 2;

  const yearlyRows = React.useMemo(() => {
    if (!filteredYearlyResults) return [];
    const months = getYearMonthsFromReportingMonth(effectiveSelectedMonth) as ReportingMonth[];
    const resultMap = new Map(
      filteredYearlyResults.map((entry) => [entry.reportingMonth, entry.result] as const),
    );

    return months.map((reportingMonth, index) => {
      const result = resultMap.get(reportingMonth);
      const submissionCounts = result?.submissions
        ? result.submissions.reduce(
            (counts, submission) => {
              if (submission.crossAudit === true || (submission as any).isCrossAudit === true) {
                counts.crossAudit += 1;
              } else {
                counts.normal += 1;
              }
              return counts;
            },
            { normal: 0, crossAudit: 0 },
          )
        : { normal: 0, crossAudit: 0 };
      const crossAuditSources = result?.submissions
        ? (() => {
            const counts = new Map<string, number>();
            result.submissions.forEach((submission) => {
              if (submission.crossAudit === true || (submission as any).isCrossAudit === true) {
                const audited = resolveDepartmentDisplayName(
                  safeText((submission as { auditedDepartmentId?: unknown }).auditedDepartmentId),
                  safeText(
                    (submission as { auditedDepartmentName?: unknown }).auditedDepartmentName ??
                      submission.departmentName,
                  ),
                  departmentIdToName,
                );
                const auditor = resolveDepartmentDisplayName(
                  safeText((submission as { auditorDepartmentId?: unknown }).auditorDepartmentId),
                  safeText((submission as { auditorDepartmentName?: unknown }).auditorDepartmentName),
                  departmentIdToName,
                );
                const key = `${audited}|${auditor}`;
                counts.set(key, (counts.get(key) ?? 0) + 1);
              }
            });
            if (!counts.size) return null;
            return Array.from(counts.entries())
              .map(([key, count]) => {
                const sep = '|';
                const idx = key.indexOf(sep);
                const audited = idx >= 0 ? key.slice(0, idx) : key;
                const auditor = idx >= 0 ? key.slice(idx + sep.length) : '';
                const label = `${audited} ตรวจไขว้โดย ${auditor}`;
                return count > 1 ? `${label} (${count})` : label;
              })
              .join(', ');
          })()
        : null;
      const departmentSources = result?.submissions
        ? (() => {
            const seen = new Map<string, string>();
            result.submissions.forEach((submission) => {
            const name = resolveSubmissionDepartmentDisplayName(submission, departmentIdToName);
              if (name && !seen.has(name)) {
                seen.set(name, name);
              }
            });
            if (!seen.size) return null;
            return Array.from(seen.keys()).join(', ');
          })()
        : null;
      return {
        index: index + 1,
        reportingMonth,
        monthLabel: formatThaiMonthYYBuddhist(reportingMonth),
        submissionSummary: crossAuditSources
          ? `ส่งเอง (${submissionCounts.normal}), ${crossAuditSources}`
          : `ส่งเอง (${submissionCounts.normal}), ตรวจไขว้ (${submissionCounts.crossAudit})`,
        normalCount: submissionCounts.normal,
        crossAuditCount: submissionCounts.crossAudit,
        crossAuditSources,
        departmentSources,
        scores: metricColumns.map((question) =>
          result?.averages[(question as { scoreKey: string }).scoreKey] ??
          result?.averages[(question as { fallbackScoreKey?: string }).fallbackScoreKey ?? ''] ??
          null,
        ),
        counts: metricColumns.map((question) =>
          result?.totals[(question as { countKey: string }).countKey] ??
          result?.totals[(question as { fallbackCountKey?: string }).fallbackCountKey ?? ''] ??
          null,
        ),
        monthlyCompletenessPct: pooledMonthlyCompletenessPct(result),
        diseaseName: isMedErrorSupervisionTable
          ? null
          : (result?.submissions && result.submissions.length > 0
            ? (() => {
                const { unique, counts } = getDiseaseCountsFromSubmissions(result!.submissions);
                return unique.length > 0 ? { unique, counts } : null;
              })()
            : null) ?? (selectedCategoryLabel ? { unique: [selectedCategoryLabel], counts: new Map([[selectedCategoryLabel, 1]]) } : null),
        consistencyPct: isMedErrorSupervisionTable
          ? null
          : result?.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT] != null &&
              Number.isFinite(result.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT])
            ? result.averages[METRICS_KEY_OVERALL_CONSISTENCY_PCT]
            : result?.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT] != null &&
                Number.isFinite(result.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT])
              ? result.averages[METRICS_KEY_CHART1_CONSISTENCY_PCT]
              : null,
        diseaseScore: isMedErrorSupervisionTable
          ? null
          : result?.averages[EXTRA.diseaseScoreKey] != null &&
              Number.isFinite(result.averages[EXTRA.diseaseScoreKey])
            ? result.averages[EXTRA.diseaseScoreKey]
            : null,
      };
    });
  }, [
    selectedCategoryLabel,
    effectiveSelectedMonth,
    filteredYearlyResults,
    metricColumns,
    isMedErrorSupervisionTable,
    departmentIdToName,
  ]);

  const yearlyTotals = React.useMemo(
    () =>
      metricColumns.map((_question, index) =>
        yearlyRows.reduce((sum, row) => sum + (row.counts[index] ?? 0), 0),
      ),
    [yearlyRows, metricColumns],
  );

  const yearlyAverages = React.useMemo(
    () =>
      metricColumns.map((_question, index) => {
        const values = yearlyRows
          .map((row) => row.scores[index])
          .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
        if (!values.length) return null;
        return values.reduce((sum, value) => sum + value, 0) / values.length;
      }),
    [yearlyRows, metricColumns],
  );

  const periodTabMonths = React.useMemo((): ReportingMonth[] => {
    if (effectiveView === 'trimester') return getFiscalQuarterMonths(effectiveSelectedMonth);
    if (effectiveView === 'year' || effectiveView === 'yearly') {
      return getYearMonthsFromReportingMonth(effectiveSelectedMonth) as ReportingMonth[];
    }
    return [];
  }, [effectiveView, effectiveSelectedMonth]);

  const periodTabKey = periodTabMonths.join('|');
  React.useEffect(() => {
    if (periodTabMonths.length === 0) {
      setSummaryMonthTab(null);
      return;
    }
    setSummaryMonthTab((prev) => (prev && periodTabMonths.includes(prev) ? prev : periodTabMonths[0]));
  }, [periodTabKey]);

  React.useEffect(() => {
    setMonthDetailCache({});
    monthDetailLoadedRef.current.clear();
  }, [periodTabKey, effectiveCategoryId, refreshKey]);

  const activeSummaryMonth = React.useMemo((): ReportingMonth | null => {
    if (effectiveView === 'monthly') return effectiveSelectedMonth;
    if (periodTabMonths.length === 0) return null;
    if (summaryMonthTab && periodTabMonths.includes(summaryMonthTab)) {
      return summaryMonthTab;
    }
    return periodTabMonths[0];
  }, [effectiveView, effectiveSelectedMonth, periodTabMonths, summaryMonthTab]);

  const submissionsForDeptSummary = React.useMemo(() => {
    const activeMonth = effectiveView === 'monthly' ? effectiveSelectedMonth : activeSummaryMonth;
    if (!activeMonth) return [];

    const canonActive = canonicalReportingMonth(activeMonth, activeMonth);
    let rows: ReportingSubmission[] = [];
    if (
      (effectiveView === 'yearly' || effectiveView === 'year' || effectiveView === 'trimester') &&
      filteredYearlyResults
    ) {
      const entry = filteredYearlyResults.find(
        (r) => canonicalReportingMonth(r.reportingMonth, canonActive) === canonActive,
      );
      const cached = monthDetailCache[canonActive];
      if (cached?.length) rows = cached;
      else if (entry?.result.submissions?.length) rows = entry.result.submissions;
      else rows = [];
    } else {
      const subs = displayResult?.submissions ?? [];
      rows = subs.filter(
        (s) => canonicalReportingMonth(s.reportingMonth ?? canonActive, canonActive) === canonActive,
      );
    }
    rows = filterReportingSubmissionsByClientDepartments(rows, shellNurseNotesDeptIds);
    const visible = filterSubmissionsForUi(rows);
    const sirenReporting =
      effectiveCategoryId === 'siren_bundle' || effectiveCategoryId === 'SIREN bundle';
    if (!sirenReporting) return visible;
    return visible.filter((row) => !isSirenBundleDepartmentExcluded(row.departmentId));
  }, [
    effectiveView,
    effectiveSelectedMonth,
    activeSummaryMonth,
    filteredYearlyResults,
    displayResult,
    monthDetailCache,
    shellNurseNotesDeptIds,
    effectiveCategoryId,
  ]);

  const departmentSummaryRows = React.useMemo(() => {
    if (!submissionsForDeptSummary.length) return [];

    const byDept = new Map<
      string,
      { label: string; subs: ReportingSubmission[] }
    >();

    for (const s of submissionsForDeptSummary) {
      const raw = s as Record<string, unknown>;
      const isCross = !!(s.isCrossAudit || raw.crossAudit === true);
      const auditedId = safeText((s as { auditedDepartmentId?: string }).auditedDepartmentId, '');
      const auditedName = safeText((s as { auditedDepartmentName?: string }).auditedDepartmentName, '');
      const norm = isCross
        ? departmentIdScopeKey((auditedId || auditedName).trim())
        : departmentIdScopeKey(safeText(s.departmentId ?? s.departmentName ?? '', '').trim());
      if (isDepartmentHiddenInUi(norm)) continue;
      const label = resolveSubmissionDepartmentDisplayName(s, departmentIdToName);
      const key = norm || label;
      if (!key) continue;
      const cur = byDept.get(key) ?? { label, subs: [] };
      cur.subs.push(s);
      if (label && label !== '-') cur.label = label;
      byDept.set(key, cur);
    }

    const lofByDeptKey = new Map(
      departments.map((d) => [departmentIdScopeKey(d.id), d.lofId] as const),
    );

    return Array.from(byDept.entries())
      .map(([deptKey, { label, subs }]) => {
        const agg = computeAggregates(subs);
        return {
          key: deptKey,
          label,
          completeness: pooledMonthlyCompletenessPct({
            submissions: subs,
            totals: agg.totals,
            averages: agg.averages,
            months: [],
          }),
          diseaseCompleteness: pooledDiseaseCompletenessPct(subs),
          consistency: pooledConsistencyPctFromSubmissions(subs),
        };
      })
      .sort((a, b) =>
        compareDepartmentsByLofThenName(
          { id: a.key, name: a.label, lofId: lofByDeptKey.get(a.key) ?? '' },
          { id: b.key, name: b.label, lofId: lofByDeptKey.get(b.key) ?? '' },
        ),
      );
  }, [submissionsForDeptSummary, departmentIdToName, departments]);

  // Default to nursing category so data loads on first view (was: empty categoryId caused early return, no fetch)
  React.useEffect(() => {
    if (!categoryOptions.length) return;
    const isValid =
      isEthicCategory(categoryId) ||
      (isOrgClimateCategory(categoryId) && canViewOrgClimate) ||
      categoryOptions.some(
        (option) => option.id === categoryId || option.title === categoryId,
      );
    if (isValid) return;
    const defaultCategory =
      categoryOptions.find((option) => option.title === 'บันทึกทางการพยาบาล') ?? categoryOptions[0];
    setCategoryId(defaultCategory?.id ?? defaultCategory?.title ?? '');
  }, [categoryOptions, categoryId, canViewOrgClimate]);

  React.useEffect(() => {
    if (
      categoryId &&
      !isEthicCategory(categoryId) &&
      !categoryOptions.some((option) => option.title === categoryId || option.id === categoryId)
    ) {
      const fallbackCategory =
        categoryOptions.find((option) => option.title === 'บันทึกทางการพยาบาล') ?? categoryOptions[0];
      setCategoryId(fallbackCategory?.id ?? fallbackCategory?.title ?? '');
    }
  }, [categoryId, categoryOptions]);

  React.useEffect(() => {
    if (
      selectedDepartmentId &&
      !scopedDepartmentsForUser.some(
        (d) => (d.id ?? d.name ?? '') === selectedDepartmentId,
      )
    ) {
      setSelectedDepartmentId('');
    }
  }, [selectedDepartmentId, scopedDepartmentsForUser]);

  React.useEffect(() => {
    // Normal page + non-global/non-supervisor roles (e.g. HEAD_WARD_NURSE):
    // never stay on implicit "all departments".
    if (mode !== 'default' || canUseAllDepartmentDefault) return;
    if (selectedDepartmentId) return;
    const first = scopedDepartmentsForUser[0];
    const firstId = safeText(first?.id ?? (first as { name?: unknown } | undefined)?.name);
    if (firstId) {
      setSelectedDepartmentId(firstId);
    }
  }, [mode, canUseAllDepartmentDefault, selectedDepartmentId, scopedDepartmentsForUser]);

  const scopedDepartmentFirstId = React.useMemo(() => {
    const first = scopedDepartmentsForUser[0];
    return safeText(first?.id ?? (first as { name?: unknown } | undefined)?.name);
  }, [scopedDepartmentsForUser]);

  const reportingScopeCategoryId = React.useMemo(
    () =>
      categoryOptions.find((o) => o.id === effectiveCategoryId || o.title === effectiveCategoryId)?.id ??
      effectiveCategoryId,
    [categoryOptions, effectiveCategoryId],
  );

  const effectiveReportingScope = React.useMemo<ReportingScopeOverrides | undefined>(() => {
    return resolveNurseNotesReportingScope({
      variant: 'reportingTable',
      mode,
      categoryId: reportingScopeCategoryId,
      reportingScope,
      selectedDepartmentIds: shellNurseNotesDeptIds,
      useSharedFiltersStickySubset: useSharedFiltersNurseNotes || shellNurseNotesDeptIds.length > 0,
      isSupervisorNurse,
      hasWildcardScope,
      teamCategoryHospitalWide,
      userAssignedDepartmentIdsForFirestore,
      selectedDepartmentId,
      scopedDepartmentFirstId,
      canUseAllDepartmentDefault,
    });
  }, [
    mode,
    reportingScope,
    selectedDepartmentId,
    userAssignedDepartmentIdsForFirestore,
    isSupervisorNurse,
    canUseAllDepartmentDefault,
    scopedDepartmentFirstId,
    teamCategoryHospitalWide,
    useSharedFiltersNurseNotes,
    shellNurseNotesDeptIds,
    reportingScopeCategoryId,
    hasWildcardScope,
  ]);

  React.useEffect(() => {
    if (effectiveView === 'monthly' || !activeSummaryMonth || !filteredYearlyResults) return;

    const canonActive = canonicalReportingMonth(activeSummaryMonth, activeSummaryMonth);
    const entry = filteredYearlyResults.find(
      (r) => canonicalReportingMonth(r.reportingMonth, canonActive) === canonActive,
    );
    if (!entry || !hasReportingAggregateData(entry.result)) return;
    if (entry.result.submissions?.length) return;
    if (monthDetailCache[canonActive]?.length) return;
    if (monthDetailLoadedRef.current.has(canonActive)) return;

    let cancelled = false;
    setMonthDetailLoading(true);
    void fetchMonthlyReportingData(
      activeSummaryMonth,
      effectiveCategoryId || undefined,
      roleScope,
      roleContext,
      effectiveReportingScope,
      { ...effectiveReportingOptions, fetchMode: 'detail' },
    )
      .then((detail) => {
        if (cancelled) return;
        if (detail.submissions.length > 0) {
          setMonthDetailCache((prev) => ({ ...prev, [canonActive]: detail.submissions }));
        }
        // Mark complete even when empty so we do not spin forever on cancel/re-entry races.
        monthDetailLoadedRef.current.add(canonActive);
      })
      .catch(() => {
        // Allow retry on next effect run.
      })
      .finally(() => {
        if (!cancelled) setMonthDetailLoading(false);
      });

    return () => {
      cancelled = true;
      // Critical: cancelled fetches used to leave loading=true forever while also
      // keeping the month in loadedRef (early-return), so the table never left "กำลังโหลด".
      setMonthDetailLoading(false);
    };
  }, [
    activeSummaryMonth,
    effectiveView,
    filteredYearlyResults,
    effectiveCategoryId,
    roleScope,
    roleContext,
    effectiveReportingScope,
    effectiveReportingOptions,
    monthDetailCache,
  ]);

  const ethicDepartmentIds = React.useMemo(() => {
    const fromOverrides = sanitizeScopedDepartmentIds(
      (effectiveReportingScope as { departmentIds?: string[] } | undefined)?.departmentIds,
    );

    if (teamCategoryHospitalWide) return ['*'];

    const hwnBase = (): string[] => {
      const fromRoleScope = sanitizeScopedDepartmentIds([
        ...(roleScope?.view?.departmentIds ?? []),
        ...(roleScope?.departmentIds ?? []),
      ]);
      if (fromRoleScope.length) return fromRoleScope;
      return sanitizeScopedDepartmentIds(userAssignedDepartmentIdsForFirestore);
    };

    if (isHeadWardNurse) {
      const base = hwnBase();
      if (fromOverrides.length && !fromOverrides.includes('*')) {
        if (base.length) {
          const baseNormSet = new Set(base.map((id) => normalizeDepartmentId(id)));
          const inter = fromOverrides.filter((id) => baseNormSet.has(normalizeDepartmentId(id)));
          if (inter.length) return inter;
          return base;
        }
        return fromOverrides;
      }
      if (base.length) return base;
      return [];
    }

    // SN: Auth LOF scope is authoritative — never let ComplianceHub overrides widen the set.
    if (isSupervisorNurse) {
      const authCap = sanitizeScopedDepartmentIds(
        effectiveLofDepartmentIds.length
          ? effectiveLofDepartmentIds
          : [
              ...(roleScope?.view?.departmentIds ?? []),
              ...(roleScope?.departmentIds ?? []),
            ].filter((id) => typeof id === 'string' && id !== '*'),
      );
      if (authCap.length) {
        if (fromOverrides.length && !fromOverrides.includes('*')) {
          const capSet = new Set(authCap.map((id) => normalizeDepartmentId(id)));
          const narrowed = fromOverrides.filter((id) => capSet.has(normalizeDepartmentId(id)));
          return narrowed.length ? narrowed : authCap;
        }
        return authCap;
      }
      if (fromOverrides.length && !fromOverrides.includes('*')) return fromOverrides;
      if (scopedDepartments.length) {
        return sanitizeScopedDepartmentIds(scopedDepartments.map((d) => d.id));
      }
      const snRoleScope = sanitizeScopedDepartmentIds([
        ...(roleScope?.view?.departmentIds ?? []),
        ...(roleScope?.departmentIds ?? []),
      ]);
      if (snRoleScope.length) return snRoleScope;
      const snUser = sanitizeScopedDepartmentIds(userAssignedDepartmentIdsForFirestore);
      if (snUser.length) return snUser;
      return [];
    }

    if (fromOverrides.length) {
      if (fromOverrides.includes('*')) {
        const expanded = sanitizeScopedDepartmentIds(scopedDepartments.map((d) => d.id));
        if (expanded.length) return expanded;
      }
      return fromOverrides;
    }

    const fromRoleScope = sanitizeScopedDepartmentIds([
      ...(roleScope?.view?.departmentIds ?? []),
      ...(roleScope?.departmentIds ?? []),
    ]);
    if (fromRoleScope.length) return fromRoleScope;

    const fromUserAssigned = sanitizeScopedDepartmentIds(userAssignedDepartmentIdsForFirestore);
    if (fromUserAssigned.length) return fromUserAssigned;

    return [];
  }, [
    effectiveReportingScope,
    isHeadWardNurse,
    isSupervisorNurse,
    effectiveLofDepartmentIds,
    teamCategoryHospitalWide,
    roleScope?.departmentIds,
    roleScope?.view?.departmentIds,
    scopedDepartments,
    userAssignedDepartmentIdsForFirestore,
  ]);

  React.useEffect(() => {
  }, [
    mode,
    view,
    selectedMonth,
    categoryId,
    selectedDepartmentId,
    effectiveReportingScope,
    roleScope?.departmentIds,
    roleScope?.view?.departmentIds,
    userAssignedDepartmentIdsForFirestore.length,
    ethicDepartmentIds,
  ]);

  const loadData = React.useCallback(async () => {
    const effectiveCat = effectiveCategoryId || categoryId;
    if (effectiveCat === 'ethicSurveys' || effectiveCat === 'orgClimateSurveys') {
      setLoading(false);
      setResult(null);
      setYearlyResults(null);
      return;
    }
    if (isMedErrorIncidentCategory(effectiveCat)) {
      setLoading(false);
      setResult(null);
      setYearlyResults(null);
      return;
    }
    if (isFallIncidentCategory(effectiveCat)) {
      setLoading(false);
      setResult(null);
      setYearlyResults(null);
      return;
    }
    // Proceed for team mode, empty category (ทั้งหมด), or any specific category (nurse_notes, etc.)
    setLoading(true);
    setError(null);
    setYearlyResults(null);
    try {
      if (effectiveView === 'yearly') {
        const data = await getYearlyReportingResults({
          reportingMonth: effectiveSelectedMonth,
          category: effectiveCategoryId || undefined,
          roleScope,
          roleContext,
          overrides: effectiveReportingScope,
          options: effectiveReportingOptions,
        });
        const totalSubs = data?.reduce((sum, r) => sum + (r.result?.submissions?.length ?? 0), 0) ?? 0;
        if (import.meta.env.DEV) {
          console.info('[ReportingTable] getYearlyReportingResults returned', {
            totalSubmissions: totalSubs,
            monthCount: data?.length ?? 0,
            sampleMonthSubs: data?.[0]?.result?.submissions?.length ?? 0,
          });
        }
        setYearlyResults(data);
        setResult(null);
      } else if (effectiveView === 'year') {
        const data = await getYearlyReportingResults({
          reportingMonth: effectiveSelectedMonth,
          category: effectiveCategoryId || undefined,
          roleScope,
          roleContext,
          overrides: effectiveReportingScope,
          options: effectiveReportingOptions,
        });
        setYearlyResults(data);
        const mergedSubs = data.flatMap((x) => x.result.submissions);
        const { totals, averages } = computeAggregates(mergedSubs);
        setResult({
          submissions: mergedSubs,
          totals,
          averages,
          months: data.map((d) => d.reportingMonth),
        });
      } else if (effectiveView === 'trimester') {
        const months = getFiscalQuarterMonths(effectiveSelectedMonth);
        const entries = await getReportingResultsForMonths(months, {
          reportingMonth: effectiveSelectedMonth,
          category: effectiveCategoryId || undefined,
          roleScope,
          roleContext,
          overrides: effectiveReportingScope,
          options: { ...effectiveReportingOptions, fetchMode: 'summary' },
        });
        const combined = entries.map(({ reportingMonth, result }) => ({
          reportingMonth,
          result,
        }));
        const results = combined.map((c) => c.result);
        setYearlyResults(combined);
        const mergedSubs = results.flatMap((r) => r.submissions);
        const { totals, averages } = computeAggregates(mergedSubs);
        setResult({
          submissions: mergedSubs,
          totals,
          averages,
          months,
        });
      } else {
        const data = await fetchReportingData(
          effectiveView,
          effectiveSelectedMonth,
          effectiveCategoryId || undefined,
          roleScope,
          roleContext,
          effectiveReportingScope,
          effectiveReportingOptions,
        );
        setResult(data);
      }
    } catch (err) {
      const errMessage = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
      const causeMessage =
        err instanceof Error && err.cause instanceof Error ? err.cause.message : '';
      const fullMsg = `${errMessage} ${causeMessage}`;
      const isBlocked =
        fullMsg.includes('ERR_BLOCKED_BY_CLIENT') ||
        fullMsg.includes('Failed to fetch') ||
        fullMsg.toLowerCase().includes('network');
      const message = isBlocked
        ? 'การเชื่อมต่อกับฐานข้อมูลถูกบล็อก (อาจเป็นจากโปรแกรมบล็อกโฆษณา) กรุณาปิดหรือลองในโหมดส่วนตัว'
        : reportingDashboardErrorMessage(err);
      setError(message);
      if (!isExtensionNoiseError(err)) {
        console.error('Failed to load reporting data', err);
      }
    } finally {
      setLoading(false);
    }
  }, [
    effectiveCategoryId,
    refreshKey,
    roleContext,
    roleScope,
    effectiveSelectedMonth,
    effectiveView,
    mode,
    effectiveReportingOptions,
    effectiveReportingScope,
  ]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const sharedLoadTrigger = sharedFilters?.loadTrigger ?? 0;
  React.useEffect(() => {
    if (useSharedFiltersNurseNotes && sharedLoadTrigger > 0) {
      loadData();
    }
  }, [sharedLoadTrigger, useSharedFiltersNurseNotes, loadData]);

  if (isMedErrorIncidentCategory(effectiveCategoryId)) {
    // When hideInternalFilters (SUPERVISOR_REPORTING_TAB), Dashboard already renders
    // MedErrorIncidentDashboard. Avoid duplication by showing placeholder here.
    if (hideInternalFilters) {
      return (
        <div className="p-6 text-center text-slate-500 text-sm">
          ข้อมูลอุบัติการณ์ความคลาดเคลื่อนทางยาแสดงในส่วน Dashboard ด้านบน
        </div>
      );
    }
    return (
      <div className="p-6">
        <Suspense fallback={categoryDashboardFallback}>
          <MedErrorIncidentDashboard
            departmentIds={['*']}
            roleScope={roleScope}
            roleContext={roleContext}
            useSharedFilters={hideInternalFilters && !!sharedFilters}
            controlledDeptFilter={hideInternalFilters && sharedFilters ? sharedFilters.medErrorDeptFilter : undefined}
            onDeptFilterChange={hideInternalFilters && sharedFilters ? sharedFilters.setMedErrorDeptFilter : undefined}
          />
        </Suspense>
      </div>
    );
  }

  if (isFallIncidentCategory(effectiveCategoryId)) {
    if (hideInternalFilters) {
      return (
        <div className="p-6 text-center text-slate-500 text-sm">
          ข้อมูลการพลัดตกหกล้มแสดงในส่วน Dashboard ด้านบน
        </div>
      );
    }
    return (
      <div className="p-6">
        <Suspense fallback={categoryDashboardFallback}>
          <FallIncidentDashboard />
        </Suspense>
      </div>
    );
  }

  if (isOrgClimateCategory(effectiveCategoryId)) {
    if (!canViewOrgClimate) {
      return (
        <div className="p-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            คุณไม่มีสิทธิ์ดูแบบประเมินบรรยากาศจริยธรรมในองค์การพยาบาล
          </div>
        </div>
      );
    }
    return (
      <div className="p-6">
        {!hideInternalFilters && (
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div>
              <p className="text-xl font-semibold text-slate-800">ตารางรายงาน</p>
              <p className="text-sm text-slate-500">{ORG_CLIMATE_LABEL} (อ่านอย่างเดียว)</p>
            </div>
          </div>
        )}
        <Suspense fallback={categoryDashboardFallback}>
          <OrgClimateReportingTable
            useSharedFilters={hideInternalFilters && !!sharedFilters}
            controlledDeptFilter={sharedFilters?.ethicDeptFilter}
            controlledDateFrom={sharedFilters?.dateFrom}
            controlledDateTo={sharedFilters?.dateTo}
            controlledPositionFilter={sharedFilters?.positionFilter}
            controlledTenureFilter={sharedFilters?.tenureFilter}
          />
        </Suspense>
      </div>
    );
  }

  if (isEthicCategory(effectiveCategoryId)) {
    return (
      <div className="p-6">
        {!hideInternalFilters && (
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div>
              <p className="text-xl font-semibold text-slate-800">ตารางรายงาน</p>
              <p className="text-sm text-slate-500">บรรยากาศองค์กรจริยธรรม (อ่านอย่างเดียว)</p>
            </div>
            <label className="flex flex-col text-sm text-slate-700">
              หมวดหมู่
              <select
                className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                value={categoryId}
                onChange={(e) => setCategoryId(normalizeCategoryId(e.target.value))}
              >
                {categoryOptions.map((option) => {
                  const value = safeText(option.id ?? option.title);
                  const label = visibleCategoryLabel(option.title, option.id);
                  return (
                    <option key={value || label} value={value}>
                      {label}
                    </option>
                  );
                })}
                {!hasEthicCategoryOption && (
                  <option value="ethicSurveys">{ethicTitle}</option>
                )}
              </select>
            </label>
          </div>
        )}
        <Suspense fallback={categoryDashboardFallback}>
          <EthicReportingTable
            departmentIds={ethicDepartmentIds}
            roleContext={roleContext}
            roleScope={roleScope}
            useSharedFilters={hideInternalFilters && !!sharedFilters}
            controlledDeptFilter={sharedFilters?.ethicDeptFilter}
            onDeptFilterChange={sharedFilters?.setEthicDeptFilter}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="p-6">
      {!hideInternalFilters && (
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xl font-semibold text-slate-800">ตารางรายงาน</p>
            <p className="text-sm text-slate-500">ข้อมูลถูกจำกัดตามบทบาทและหน่วยงานที่คุณเข้าถึงได้</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {useShellNurseNotesPeriod ? null : (
            <>
            <label className="flex flex-col text-sm text-slate-700">
              มุมมอง
              <select
                className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                value={view}
                onChange={(e) => {
                  const nextView = e.target.value as ViewMode;
                  setView(nextView);
                  const [y, m] = selectedMonth.split('-').map(Number);
                  const year = Number.isFinite(y) ? y : new Date().getFullYear();
                  if (nextView === 'yearly') {
                    setSelectedMonth(`${year}-01` as ReportingMonth);
                  } else if (nextView === 'trimester') {
                    setSelectedMonth(reportingMonthToFiscalQuarterAnchor(selectedMonth));
                  }
                }}
              >
                <option value="monthly">รายเดือน</option>
                <option value="trimester">รายไตรมาส</option>
                <option value="yearly">รายปี</option>
              </select>
            </label>
            {view === 'yearly' ? (
              <label className="flex flex-col text-sm text-slate-700">
                ปี
                <select
                  className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={selectedMonth.substring(0, 4)}
                  onChange={(e) => setSelectedMonth(`${e.target.value}-01` as ReportingMonth)}
                >
                  {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map((yr) => (
                    <option key={yr} value={yr}>
                      {buddhistFiscalYear(yr)}
                    </option>
                  ))}
                </select>
              </label>
            ) : view === 'trimester' ? (
              <>
                  <label className="flex flex-col text-sm text-slate-700">
                    ปี
                    <select
                      className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={reportingMonthToFiscalYearQuarter(selectedMonth).fiscalYear}
                      onChange={(e) => {
                        const { quarter } = reportingMonthToFiscalYearQuarter(selectedMonth);
                        const nextYear = Number(e.target.value);
                        setSelectedMonth(fiscalYearQuarterToAnchorReportingMonth(nextYear, quarter));
                      }}
                    >
                      {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map((yr) => (
                        <option key={yr} value={yr}>
                          {buddhistFiscalYear(yr)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col text-sm text-slate-700">
                    ไตรมาส
                    <select
                      className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={reportingMonthToFiscalYearQuarter(selectedMonth).quarter}
                      onChange={(e) => {
                        const q = Number(e.target.value) as FiscalQuarter;
                        const { fiscalYear } = reportingMonthToFiscalYearQuarter(selectedMonth);
                        setSelectedMonth(fiscalYearQuarterToAnchorReportingMonth(fiscalYear, q));
                      }}
                    >
                    {([1, 2, 3, 4] as const).map((q) => (
                      <option key={q} value={q}>
                        {fiscalQuarterOptionLabel(reportingMonthToFiscalYearQuarter(selectedMonth).fiscalYear, q)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <label className="flex flex-col text-sm text-slate-700">
                เดือนรายงาน
                <input
                  type="month"
                  className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value as ReportingMonth)}
                />
              </label>
            )}
            </>
            )}
            <label className="flex flex-col text-sm text-slate-700">
              หมวดหมู่
              <select
                className="mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                value={categoryId}
                onChange={(e) => setCategoryId(normalizeCategoryId(e.target.value))}
              >
                {categoryOptions.map((option) => {
                  const value = safeText(option.id ?? option.title);
                  const label = visibleCategoryLabel(option.title, option.id);
                  const periodicityLabel = formatPeriodicityLabel(option.periodicity);
                  return (
                    <option key={value || label} value={value}>
                      {label}
                      {periodicityLabel ? ` (${periodicityLabel})` : ''}
                    </option>
                  );
                })}
                {!hasEthicCategoryOption && (
                  <option value="ethicSurveys">
                    {ethicTitle}
                    {ethicPeriodicityLabel ? ` (${ethicPeriodicityLabel})` : ''}
                  </option>
                )}
              </select>
            </label>
            <button
              className="h-fit self-end rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? 'กำลังโหลด...' : 'ดึงข้อมูล'}
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

      {mode === 'default' && isSupervisorNurse && scopedDepartmentsForUser.length === 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-semibold">ไม่พบหน่วยงานในสายงาน</p>
          <p className="mt-1">
            กรุณาตรวจสอบว่าบัญชีถูกเชื่อมสายผู้ตรวจการพยาบาลในระบบ (Firestore) หรือรันสคริปต์ backfill
          </p>
        </div>
      )}

      {emptyStateVisible && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-4 text-sm text-slate-800">
          <p className="font-semibold text-amber-900">ยังไม่มีการส่งข้อมูล (0 submissions)</p>
          <p className="mt-2 text-slate-700">
            ยังไม่มีรายงานสำหรับช่วงและหมวดหมู่นี้
            {scopedDepartmentLabels.length > 0
              ? ` ในหน่วยงาน${scopedDepartmentLabels.length > 1 ? ` (${scopedDepartmentLabels.length} หน่วยงาน)` : ''}ที่คุณเข้าถึงได้`
              : ''}
            {' '}กรุณาลอง:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-700">
            <li>เลือกเดือนหรือหมวดหมู่อื่น</li>
            <li>ส่งข้อมูลผ่านแบบฟอร์ม Compliance หรือ Compliance Hub สำหรับหมวดหมู่นี้</li>
            <li>ตรวจสอบว่าหน่วยงานที่เลือกมี submission ใน Firestore แล้ว (path: submissions/&#123;deptId&#125;/&#123;YYYY-MM&#125;/&#123;category&#125;)</li>
          </ul>
          <div className="mt-3 rounded border border-slate-200 bg-white/80 px-3 py-2 text-xs text-slate-600">
            <span className="font-medium">ช่วง:</span>{' '}
            {effectiveView === 'year' || effectiveView === 'yearly'
              ? `ปี ${buddhistFiscalYear(reportingMonthToFiscalYearQuarter(effectiveSelectedMonth).fiscalYear)}`
              : effectiveView === 'trimester'
                ? (() => {
                    const { fiscalYear, quarter } = reportingMonthToFiscalYearQuarter(effectiveSelectedMonth);
                    return fiscalQuarterOptionLabel(fiscalYear, quarter);
                  })()
                : formatReportingMonthLabelBuddhist(effectiveSelectedMonth)}
            {' • '}
            <span className="font-medium">หมวดหมู่:</span>{' '}
            {safeText(
              categoryOptions.find((c) => (c.id ?? c.title) === effectiveCategoryId)?.title ??
                effectiveCategoryId,
              'ทั้งหมด',
            )}
            {' • '}
            <span className="font-medium">หน่วยงาน:</span> {scopedDepartmentLabels.length}
            {scopedDepartmentLabels.length > 0 && scopedDepartmentLabels.length <= 10 && (
              <> ({scopedDepartmentLabels.join(', ')})</>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
          <div>
            <p className="text-sm text-slate-500">ช่วงข้อมูล</p>
            <p className="text-base font-semibold text-slate-800">
              {effectiveView === 'monthly' && `เดือน ${effectiveSelectedMonth}`}
              {effectiveView === 'trimester' &&
                `ไตรมาส (${getFiscalQuarterMonths(effectiveSelectedMonth).join(', ')})`}
              {(effectiveView === 'year' || effectiveView === 'yearly') &&
                `ปี ${buddhistFiscalYear(reportingMonthToFiscalYearQuarter(effectiveSelectedMonth).fiscalYear)}`}
            </p>
          </div>
          {/* Team page: "all departments" dropdown (for NN etc). Normal page supervisor_nurse: per-department dropdown with ONLY LOF departments. Others: buttons when multiple departments. */}
          {scopedDepartmentsForUser.length > 1 && !hideInternalFilters && (
            mode === 'team' ? (
              <select
                value={selectedDepartmentId}
                onChange={(e) => setSelectedDepartmentId(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm"
              >
                <option value="">ทั้งหมด</option>
                {scopedDepartmentsForUser.map((dept) => {
                  const value = safeText(dept.id ?? (dept as { name?: unknown }).name);
                  const label = resolveDepartmentDisplayName(value, safeText((dept as { name?: unknown }).name));
                  return (
                    <option key={value || label} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            ) : mode === 'default' && isSupervisorNurse ? (
              <select
                value={selectedDepartmentId}
                onChange={(e) => setSelectedDepartmentId(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm"
                title="หน่วยงานในสายงานของคุณ"
              >
                <option value="">ทั้งหมดในสายงาน</option>
                {scopedDepartmentsForUser.map((dept) => {
                  const value = safeText(dept.id ?? (dept as { name?: unknown }).name);
                  const label = resolveDepartmentDisplayName(value, safeText((dept as { name?: unknown }).name));
                  return (
                    <option key={value || label} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            ) : (
              <div className="flex flex-wrap gap-1">
                {canUseAllDepartmentDefault && (
                  <button
                    type="button"
                    onClick={() => setSelectedDepartmentId('')}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                      !selectedDepartmentId
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    ทั้งหมด
                  </button>
                )}
                {scopedDepartmentsForUser.map((dept) => {
                  const value = safeText(dept.id ?? (dept as { name?: unknown }).name);
                  const label = resolveDepartmentDisplayName(value, safeText((dept as { name?: unknown }).name));
                  const isActive = selectedDepartmentId === value;
                  return (
                    <button
                      key={value || label}
                      type="button"
                      onClick={() => setSelectedDepartmentId(value)}
                      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                        isActive ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )
          )}
          <div className="text-right">
            <p className="text-sm text-slate-500">จำนวน submission</p>
            <p className="text-xl font-semibold text-slate-800">
              {showsTwelveMonthYearBreakdownTable ||
              effectiveView === 'year' ||
              effectiveView === 'trimester'
                ? filteredYearlyResults?.reduce(
                    (sum, row) => sum + getReportingSubmissionCount(row.result),
                    0,
                  ) ??
                  getReportingSubmissionCount(displayResult ?? undefined) ??
                  0
                : getReportingSubmissionCount(displayResult ?? result ?? undefined)}
            </p>
          </div>
        </div>
        {import.meta.env.DEV && (
          <p className="mt-2 text-xs text-slate-500">Loaded submissions: {result?.submissions.length ?? 0}</p>
        )}

          <div className="mt-4 space-y-4">
            {(effectiveView === 'trimester' || effectiveView === 'year' || effectiveView === 'yearly') &&
              periodTabMonths.length > 0 && (
                <div
                  className="flex flex-wrap gap-2 border-b border-slate-100 pb-3"
                  role="tablist"
                  aria-label="เลือกเดือนในช่วงที่ดู"
                >
                  {periodTabMonths.map((m) => {
                    const selected = activeSummaryMonth === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                          selected ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        onClick={() => setSummaryMonthTab(m)}
                      >
                        {formatThaiMonthYYBuddhist(m)}
                      </button>
                    );
                  })}
                </div>
              )}

            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="min-w-full border-collapse text-left text-sm text-slate-700">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <th className="px-3 py-2">หน่วยงาน</th>
                    <th className="px-3 py-2">ร้อยละความสมบูรณ์</th>
                    {showNurseNotesSummaryColumns ? (
                      <>
                        <th className="px-3 py-2">ร้อยละความสมบูรณ์รายโรค</th>
                        <th className="px-3 py-2">ร้อยละความสอดคล้อง</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {loading || sharedDashboardLoading || monthDetailLoading ? (
                    <tr>
                      <td className="px-3 py-8 text-center text-slate-500" colSpan={departmentSummaryColumnCount}>
                        {NIDATA_LOADING_LABEL}
                      </td>
                    </tr>
                  ) : departmentSummaryRows.length === 0 ? (
                    <tr>
                      <td className="px-3 py-8 text-center text-slate-500" colSpan={departmentSummaryColumnCount}>
                        ยังไม่มีข้อมูลสำหรับเดือนที่เลือก
                      </td>
                    </tr>
                  ) : (
                    departmentSummaryRows.map((row) => (
                      <tr key={row.key} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-3 py-2 font-semibold text-slate-800">{row.label}</td>
                        <td className="px-3 py-2 tabular-nums">
                          {row.completeness != null && Number.isFinite(row.completeness)
                            ? row.completeness.toFixed(2)
                            : '-'}
                        </td>
                        {showNurseNotesSummaryColumns ? (
                          <>
                            <td className="px-3 py-2 tabular-nums">
                              {row.diseaseCompleteness != null && Number.isFinite(row.diseaseCompleteness)
                                ? row.diseaseCompleteness.toFixed(2)
                                : '-'}
                            </td>
                            <td className="px-3 py-2 tabular-nums">
                              {row.consistency != null && Number.isFinite(row.consistency)
                                ? row.consistency.toFixed(2)
                                : '-'}
                            </td>
                          </>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        {showsTwelveMonthYearBreakdownTable ? (
          <div className="mt-4">
            {yearlyRows.some((r) => ((r as { crossAuditCount?: number }).crossAuditCount ?? 0) > 0) && (
              <div className="mb-2 flex justify-end">
                <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded"
                    style={{ backgroundColor: CROSS_AUDIT_COLOR }}
                  />
                  ตรวจไขว้
                </span>
              </div>
            )}
            <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-xs text-slate-700">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                  <th rowSpan={2} className="min-w-[60px] border border-slate-200 px-3 py-2 text-center">
                    ลำดับ
                  </th>
                  <th rowSpan={2} className="min-w-[90px] border border-slate-200 px-3 py-2 text-center">
                    เดือน
                  </th>
                  {metricColumns.map((question) => (
                    <th
                      key={question.label}
                      colSpan={2}
                      className="min-w-[120px] border border-slate-200 px-3 py-2 text-center"
                    >
                      {question.label}
                    </th>
                  ))}
                  <th rowSpan={2} className="min-w-[110px] border border-slate-200 px-3 py-2 text-center">
                    ร้อยละความสมบูรณ์
                  </th>
                  {!isMedErrorSupervisionTable && (
                    <>
                      <th rowSpan={2} className="min-w-[110px] border border-slate-200 px-3 py-2 text-center">
                        คะแนนรายโรค
                      </th>
                      <th rowSpan={2} className="min-w-[110px] border border-slate-200 px-3 py-2 text-center">
                        ความสอดคล้อง
                      </th>
                      <th rowSpan={2} className="min-w-[140px] border border-slate-200 px-3 py-2 text-center">
                        ชื่อรายโรค
                      </th>
                    </>
                  )}
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-500">
                  {metricColumns.map((question) => (
                    <React.Fragment key={`${question.label}-sub`}>
                      <th className="border border-slate-200 px-3 py-2 text-center">คะแนน</th>
                      <th className="border border-slate-200 px-3 py-2 text-center">*จำนวน</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {yearlyRows.map((row) => (
                  <tr key={row.reportingMonth} className="border-b border-slate-100">
                    <td className="border border-slate-200 px-3 py-2 text-center">{row.index}</td>
                    <td className="border border-slate-200 px-3 py-2 text-center">
                      {(row.submissionSummary || row.departmentSources) ? (
                        <HoverCard.Root openDelay={200} closeDelay={100}>
                          <HoverCard.Trigger asChild>
                            <div className="inline-flex cursor-help items-center justify-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400">
                              <span>{row.monthLabel}</span>
                              <Info className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden />
                            </div>
                          </HoverCard.Trigger>
                          <HoverCard.Portal>
                            <HoverCard.Content
                              className="z-50 max-w-sm rounded-lg border border-slate-200 bg-white p-3 text-left shadow-lg data-[side=bottom]:mt-1"
                              sideOffset={6}
                              collisionPadding={10}
                            >
                              {row.submissionSummary && (
                                <div className="text-[11px] text-slate-600">
                                  {(row.submissionSummary ?? '').split(', ').filter(Boolean).map((part, i) => (
                                    <span key={i}>
                                      {i > 0 && ', '}
                                      {part.startsWith('ตรวจไขว้') ? (
                                        <span style={{ color: CROSS_AUDIT_COLOR, fontWeight: 600 }}>{part}</span>
                                      ) : (
                                        part
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {row.departmentSources && (
                                <div className={`text-[11px] font-medium text-slate-700 ${row.submissionSummary ? 'mt-2' : ''}`}>
                                  แผนก: {row.departmentSources}
                                </div>
                              )}
                            </HoverCard.Content>
                          </HoverCard.Portal>
                        </HoverCard.Root>
                      ) : (
                        <div>{row.monthLabel}</div>
                      )}
                    </td>
                    {row.scores.map((score, index) => (
                      <React.Fragment key={`${row.reportingMonth}-${metricColumns[index]?.label ?? index}`}>
                        <td className="border border-slate-200 px-3 py-2 text-center">
                          {formatScoreValue(score)}
                        </td>
                        <td className="border border-slate-200 px-3 py-2 text-center">
                          {formatCountValue(row.counts[index])}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="border border-slate-200 px-3 py-2 text-center">
                      {row.monthlyCompletenessPct != null && Number.isFinite(row.monthlyCompletenessPct)
                        ? `${Number(row.monthlyCompletenessPct).toFixed(2)}%`
                        : 'NA'}
                    </td>
                    {!isMedErrorSupervisionTable && (
                      <>
                        <td className="border border-slate-200 px-3 py-2 text-center">
                          {formatScoreValue(row.diseaseScore)}
                        </td>
                        <td className="border border-slate-200 px-3 py-2 text-center">
                          {row.consistencyPct != null && Number.isFinite(row.consistencyPct)
                            ? `${Number(row.consistencyPct).toFixed(2)}%`
                            : 'NA'}
                        </td>
                        <td className="border border-slate-200 px-3 py-2 text-center">
                          {row.diseaseName && typeof row.diseaseName === 'object' && 'unique' in row.diseaseName ? (
                            (() => {
                              const { unique, counts } = row.diseaseName as { unique: string[]; counts: Map<string, number> };
                              if (!unique.length) return 'NA';
                              return (
                                <span className="inline-flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5">
                                  {unique.map((name, i) => {
                                    const count = counts.get(name) ?? 1;
                                    const tooltip = `${name} ${count} ครั้ง`;
                                    return (
                                      <span key={`${name}-${i}`}>
                                        {i > 0 && ', '}
                                        <span
                                          title={tooltip}
                                          className="cursor-help underline decoration-dotted decoration-slate-400 underline-offset-1"
                                        >
                                          {name}
                                        </span>
                                      </span>
                                    );
                                  })}
                                </span>
                              );
                            })()
                          ) : (
                            typeof row.diseaseName === 'string' ? row.diseaseName : 'NA'
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                <tr className="border-t border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                  <td className="border border-slate-200 px-3 py-2 text-center" colSpan={2}></td>
                  {yearlyTotals.map((total, index) => (
                    <React.Fragment key={`total-${metricColumns[index]?.label ?? index}`}>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                      <td className="border border-slate-200 px-3 py-2 text-center">
                        {formatCountValue(total)}
                      </td>
                    </React.Fragment>
                  ))}
                  <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                  {!isMedErrorSupervisionTable && (
                    <>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                    </>
                  )}
                </tr>
                <tr className="border-t border-slate-200 bg-white text-xs font-semibold text-slate-600">
                  <td className="border border-slate-200 px-3 py-2 text-center"></td>
                  <td className="border border-slate-200 px-3 py-2 text-center">เฉลี่ย(%)</td>
                  {yearlyAverages.map((average, index) => (
                    <React.Fragment key={`avg-${metricColumns[index]?.label ?? index}`}>
                      <td className="border border-slate-200 px-3 py-2 text-center">
                        {formatScoreValue(average)}
                      </td>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                    </React.Fragment>
                  ))}
                  <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                  {!isMedErrorSupervisionTable && (
                    <>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                      <td className="border border-slate-200 px-3 py-2 text-center">NA</td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ReportingTable;
